import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { hasLevels, LOCATION_TYPE_LABEL_HU, type LocationView } from '@persped/shared';
import { useState } from 'react';
import { useDismantleLevel, useDismantlePallet, useLocations } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * MINDEN MAPPA ÁTHELYEZÉSE — megerősítő képernyő.
 *
 * A művelet neve 2026-08-25-ig „felbontás" volt, és ez nem csak félreérthető
 * szó volt: a szerveroldali viselkedés is követte, `DISMANTLED`-re állította
 * a raklapot, amiből nem volt visszaút. A raklap NEM szűnik meg — kiürül, és
 * utána újra megtölthető.
 *
 * A spec 17.1 §-a KÖTELEZŐVÉ teszi: „Veszélyes tömeges műveleteknél
 * összesítő és megerősítő képernyő szükséges." Ezért a dialógus előre
 * kimondja, HÁNY mappa és HOVA kerül — nem csak azt kérdezi, hogy „biztos?".
 *
 * Az `expectedFolderCount` a felületen LÁTOTT darabszámot küldi vissza. Ha
 * közben változott (valaki épp pakolt rá), a szerver elutasítja. Ez fordítja
 * a megerősítést konvencióból szerver által kikényszerített invariánssá — a
 * művelet a spec 15.5 §-a szerint atomi.
 */
export function DismantleDialog({
  open,
  onClose,
  palletBarcode,
  /** Ha megvan, szintet bontunk; ha nincs, a TELJES raklapot. */
  levelNumber,
  folderCount,
}: {
  open: boolean;
  onClose: () => void;
  palletBarcode: string;
  levelNumber?: number;
  folderCount: number;
}) {
  const dismantleLevel = useDismantleLevel();
  const dismantlePallet = useDismantlePallet();
  const locations = useLocations({ status: 'ACTIVE' });
  const [target, setTarget] = useState<LocationView | null>(null);
  const [level, setLevel] = useState('');

  const mutation = levelNumber !== undefined ? dismantleLevel : dismantlePallet;
  // Szintes CÉLHELYNÉL kötelező a szint — és a polc is szintes (D fázis),
  // nem csak a raklap. A `fla` CHECK-je mindkettőt megköveteli.
  const targetNeedsLevel = target !== null && hasLevels(target.locationType);
  const targetLevelRequired = targetNeedsLevel && level === '';
  const sameAsSource = target?.barcode === palletBarcode;
  const canSubmit = target !== null && !targetLevelRequired && !sameAsSource && !mutation.isPending;

  const close = () => {
    mutation.reset();
    setTarget(null);
    setLevel('');
    onClose();
  };

  const cim =
    levelNumber !== undefined
      ? `${palletBarcode} — ${levelNumber}. szint: minden mappa áthelyezése`
      : `${palletBarcode}: minden mappa áthelyezése`;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{cim}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Az összesítő — ez a spec 17.1 §-ának lényege. */}
          <Alert severity="warning">
            <AlertTitle>Összesítő</AlertTitle>
            <Typography variant="body2">
              <strong>{folderCount} mappa</strong> kerül át
              {levelNumber !== undefined ? ` a ${levelNumber}. szintről` : ' erről a raklapról'} a
              kiválasztott célhelyre. A művelet <strong>atomi</strong>: vagy mind átkerül, vagy
              egyik sem.
            </Typography>
          </Alert>

          {folderCount === 0 && (
            <Alert severity="info">
              Jelenleg nincs itt mappa — nincs mit áthelyezni.
            </Alert>
          )}

          <Autocomplete
            options={(locations.data ?? []).filter((l) => l.barcode !== palletBarcode)}
            value={target}
            onChange={(_, next) => {
              setTarget(next);
              setLevel('');
              mutation.reset();
            }}
            loading={locations.isPending}
            getOptionLabel={(l) => `${l.barcode} — ${l.name}`}
            groupBy={(l) => LOCATION_TYPE_LABEL_HU[l.locationType]}
            isOptionEqualToValue={(a, b) => a.barcode === b.barcode}
            renderInput={(params) => <TextField {...params} label="Hova kerüljenek a mappák?" />}
          />

          {targetNeedsLevel && target.levels && (
            <TextField
              select
              label="Cél szint"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              helperText="Raklap és polc célhelynél a szint kötelező."
              fullWidth
            >
              {target.levels.map((l) => (
                <MenuItem key={l.levelNumber} value={String(l.levelNumber)}>
                  {l.levelNumber}. szint — {l.folderCount} mappa
                </MenuItem>
              ))}
            </TextField>
          )}

          {mutation.isError && <ProblemAlert error={mutation.error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Mégse</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!canSubmit || folderCount === 0}
          onClick={() => {
            if (!target) return;
            const kozos = {
              palletBarcode,
              targetLocationBarcode: target.barcode,
              targetPalletLevel: level ? Number(level) : undefined,
              expectedFolderCount: folderCount,
            };
            if (levelNumber !== undefined) {
              dismantleLevel.mutate({ ...kozos, levelNumber }, { onSuccess: close });
            } else {
              dismantlePallet.mutate(kozos, { onSuccess: close });
            }
          }}
        >
          Minden mappa áthelyezése ({folderCount} mappa)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
